"use client";

import Link from "next/link";
import Image from "next/image";
import { Building2, LogOut, Menu, User, X } from "lucide-react";
import { useState } from "react";
import type { ApplicationRole, AuthenticatedActor } from "@/contexts/identity/domain/actor";
import { signOut } from "@/app/auth/actions";

export default function Navbar({ initialUser, initialRole, initialUnreadCount = 0 }: {
  initialUser?: AuthenticatedActor | null;
  initialRole?: ApplicationRole | null;
  initialUnreadCount?: number;
}) {
  const user = initialUser ?? null;
  const role = initialRole ?? null;
  const [open, setOpen] = useState(false);
  const dashboardHref = role === "contractor" ? "/dashboard/company" : role === "admin" ? "/admin" : "/leads";
  const dashboardLabel = role === "contractor" ? "시공사 대시보드" : role === "admin" ? "관리자 대시보드" : "내 부지";

  const links = (
    <>
      <Link href="/about" className="rounded-lg px-3 py-2 text-sm font-medium text-[#4e5968] hover:bg-secondary hover:text-foreground">서비스 소개</Link>
      {user && <Link href={dashboardHref} className="rounded-lg px-3 py-2 text-sm font-medium text-[#4e5968] hover:bg-secondary hover:text-foreground">{dashboardLabel}</Link>}
      {user && role !== "admin" && (
        <Link href="/dashboard/inquiries" className="relative rounded-lg px-3 py-2 text-sm font-medium text-[#4e5968] hover:bg-secondary hover:text-foreground">
          운영자 문의
          {initialUnreadCount > 0 && <span className="ml-1 rounded-full bg-destructive px-1.5 py-0.5 text-[10px] text-white">{initialUnreadCount}</span>}
        </Link>
      )}
    </>
  );

  return (
    <nav className="sticky top-0 z-50 border-b border-border bg-card/95 backdrop-blur-xl">
      <div className="mx-auto flex min-h-[61px] w-full max-w-[1240px] items-center justify-between px-5 sm:px-10">
        <Link href="/" className="flex min-h-11 items-center" aria-label="쨍하고 홈">
          <Image src="/brand/logo.png" width={81} height={32} alt="쨍하고" priority />
        </Link>
        <div className="hidden items-center gap-1 md:flex">
          {links}
          {user ? (
            <div className="ml-3 flex items-center gap-3 border-l border-border pl-4">
              <span className="flex max-w-44 items-center gap-1.5 truncate text-xs text-muted-foreground">
                {role === "contractor" ? <Building2 size={14} /> : <User size={14} />}{user.email ?? '이메일 미제공'}
              </span>
              <form action={signOut}><button className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground"><LogOut size={15} />로그아웃</button></form>
            </div>
          ) : (
            <>
              <Link href="/auth/login" className="zzaeng-outline-action ml-3 min-h-10 px-4">고객(발전사업자) 로그인</Link>
              <Link href="/leads/new" className="zzaeng-primary-action ml-2 min-h-10 px-4">무료 분석 시작</Link>
            </>
          )}
        </div>
        <button type="button" className="grid h-11 w-11 place-items-center rounded-md hover:bg-secondary md:hidden" aria-label={open ? "메뉴 닫기" : "메뉴 열기"} onClick={() => setOpen(!open)}>{open ? <X size={22} /> : <Menu size={22} />}</button>
      </div>
      {open && (
        <div className="border-t border-border bg-card px-5 py-4 md:hidden">
          <div className="flex flex-col gap-1" onClick={() => setOpen(false)}>{links}</div>
          {user ? <form action={signOut}><button className="mt-3 flex w-full items-center gap-2 border-t border-border px-3 pt-4 text-sm text-muted-foreground"><LogOut size={16} />로그아웃</button></form> : <div className="mt-4 grid gap-2"><Link href="/auth/login" className="zzaeng-outline-action w-full">고객(발전사업자) 로그인</Link><Link href="/leads/new" className="zzaeng-primary-action w-full">무료 분석 시작</Link></div>}
        </div>
      )}
    </nav>
  );
}
